<template>
  <div>
    <!-- SECCIÓN 1: Estado del Servicio -->
    <h5 class="font-size-15 mb-3 text-primary">
      <i class="mdi mdi-toggle-switch-outline me-1"></i> Estado del servicio
    </h5>
    <div class="row g-3 mb-4">
      <div class="col-md-12">
        <div class="p-3 rounded border d-flex align-items-center justify-content-between h-100">
          <div>
            <label class="form-check-label fw-bold mb-1" for="switchActivo">
              Habilitar WhatsApp Business API
            </label>
            <div class="text-muted font-size-12">
              Permite el envío automático de notificaciones, recordatorios y mensajes por WhatsApp a los clientes de la empresa.
            </div>
          </div>
          <div class="form-check form-switch form-switch-lg ms-3">
            <input
              class="form-check-input style-pointer"
              type="checkbox"
              id="switchActivo"
              v-model="localAjustes.whatsapp_activo"
              @change="$emit('guardar')"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Alerta informativa cuando WhatsApp Business está desactivado -->
    <div class="alert alert-warning border-0 shadow-sm d-flex align-items-center mb-4" v-if="!localAjustes.whatsapp_activo">
      <i class="mdi mdi-lock-outline font-size-22 me-2 text-warning"></i>
      <div class="d-flex justify-content-between align-items-center w-100 flex-wrap gap-2">
        <span class="font-size-13">
          <strong>Servicio desactivado:</strong> WhatsApp Business API se encuentra inhabilitado. Las credenciales y las pruebas de envío están deshabilitadas. Active el interruptor para modificar las configuraciones o realizar envíos.
        </span>
        <button
          type="button"
          class="btn btn-warning btn-sm py-1 px-2 font-size-12 shadow-sm"
          @click="$emit('habilitar-servicio')"
        >
          <i class="mdi mdi-power me-1"></i> Habilitar WhatsApp Business
        </button>
      </div>
    </div>

    <!-- Fieldset que DESACTIVA todas las configuraciones cuando el servicio está apagado -->
    <fieldset :disabled="!localAjustes.whatsapp_activo" class="border-0 p-0 m-0" :class="{ 'opacity-50': !localAjustes.whatsapp_activo }">

      <hr class="my-4">

      <!-- SECCIÓN 2: Credenciales de WhatsApp Cloud API -->
      <h5 class="font-size-15 mb-3 text-primary">
        <i class="mdi mdi-shield-key-outline me-1"></i> Credenciales de WhatsApp Cloud API (Meta)
      </h5>
      <p class="text-muted font-size-13 mb-3">
        Configure las credenciales de la cuenta de WhatsApp Business para esta empresa desde
        <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer" class="text-info fw-bold">
          Meta for Developers <i class="mdi mdi-open-in-new"></i>
        </a>.
      </p>

      <div class="row g-3 mb-4">
        <div class="col-md-4">
          <label class="form-label fw-semibold">Número de teléfono emisor</label>
          <div class="input-group">
            <span class="input-group-text"><i class="mdi mdi-phone"></i></span>
            <input
              type="text"
              class="form-control"
              v-model="localAjustes.whatsapp_numero_telefono"
              placeholder="+52 55 1234 5678"
            />
          </div>
          <small class="text-muted">Número verificado asociado a la cuenta de WhatsApp Business.</small>
        </div>

        <div class="col-md-4">
          <label class="form-label fw-semibold">Phone Number ID</label>
          <div class="input-group">
            <span class="input-group-text"><i class="mdi mdi-identifier"></i></span>
            <input
              type="text"
              class="form-control"
              v-model="localAjustes.whatsapp_phone_number_id"
              placeholder="Ej. 104928374829102"
            />
          </div>
          <small class="text-muted">Identificador del número telefónico en Meta.</small>
        </div>

        <div class="col-md-4">
          <label class="form-label fw-semibold">WhatsApp Business Account ID (WABA)</label>
          <div class="input-group">
            <span class="input-group-text"><i class="mdi mdi-domain"></i></span>
            <input
              type="text"
              class="form-control"
              v-model="localAjustes.whatsapp_business_account_id"
              placeholder="Ej. 102938475610293"
            />
          </div>
          <small class="text-muted">ID de la cuenta comercial de WhatsApp.</small>
        </div>

        <div class="col-md-12">
          <label class="form-label fw-semibold">Token de Acceso Permanente (Bearer Token)</label>
          <div class="input-group">
            <span class="input-group-text"><i class="mdi mdi-key-variant"></i></span>
            <input
              :type="mostrarToken ? 'text' : 'password'"
              class="form-control"
              v-model="localAjustes.whatsapp_token"
              placeholder="EAAG..."
            />
            <button
              class="btn btn-outline-secondary"
              type="button"
              @click="mostrarToken = !mostrarToken"
              :title="mostrarToken ? 'Ocultar token' : 'Mostrar token'"
            >
              <i :class="mostrarToken ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
            </button>
            <button
              class="btn btn-outline-secondary"
              type="button"
              @click="copiarAlPortapapeles(localAjustes.whatsapp_token, 'Token copiado')"
              title="Copiar token"
            >
              <i class="mdi mdi-content-copy"></i>
            </button>
          </div>
          <small class="text-muted">Token de sistema con permisos <code>whatsapp_business_messaging</code>.</small>
        </div>

        <div class="col-md-6">
          <label class="form-label fw-semibold">App ID (Meta App)</label>
          <input
            type="text"
            class="form-control"
            v-model="localAjustes.whatsapp_app_id"
            placeholder="Ej. 7829103847192"
          />
        </div>

        <div class="col-md-6">
          <label class="form-label fw-semibold">App Secret</label>
          <div class="input-group">
            <input
              :type="mostrarSecret ? 'text' : 'password'"
              class="form-control"
              v-model="localAjustes.whatsapp_app_secret"
              placeholder="Clave secreta de la aplicación Meta"
            />
            <button
              class="btn btn-outline-secondary"
              type="button"
              @click="mostrarSecret = !mostrarSecret"
              :title="mostrarSecret ? 'Ocultar secret' : 'Mostrar secret'"
            >
              <i :class="mostrarSecret ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- SECCIÓN 3: Webhook de WhatsApp Cloud API (Meta) -->
      <div class="card border shadow-sm mb-4">
        <div class="card-header py-3 border-bottom d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <i class="mdi mdi-webhook text-success font-size-22 me-2"></i>
            <div>
              <h6 class="fw-bold mb-0 font-size-15">
                Webhook de WhatsApp Cloud API (Meta)
              </h6>
              <div class="text-muted font-size-12 mt-1">
                Configure la URL de devolución de llamada (Callback URL) y el Identificador de verificación (Verify Token) en la consola de desarrolladores de Meta para recibir actualizaciones de entrega y lectura de los tickets de pago.
              </div>
            </div>
          </div>
        </div>

        <div class="card-body p-4">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label fw-semibold">URL de devolución de llamada (Callback URL)</label>
              <div class="input-group">
                <span class="input-group-text"><i class="mdi mdi-link-variant"></i></span>
                <input
                  type="text"
                  class="form-control font-monospace font-size-13"
                  :value="urlWebhook"
                  readonly
                />
                <button
                  class="btn btn-outline-secondary"
                  type="button"
                  @click="copiarAlPortapapeles(urlWebhook, 'URL del webhook copiada')"
                  title="Copiar URL del Webhook"
                >
                  <i class="mdi mdi-content-copy"></i>
                </button>
              </div>
              <small class="text-muted">Pegue esta URL en el campo <b>URL de devolución de llamada</b> en Meta Developers.</small>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Identificador de verificación (Verify Token)</label>
              <div class="input-group">
                <span class="input-group-text"><i class="mdi mdi-shield-check"></i></span>
                <input
                  :type="mostrarWebhookToken ? 'text' : 'password'"
                  class="form-control font-monospace font-size-13"
                  v-model="localAjustes.whatsapp_webhook_token"
                  placeholder="Haga clic en 'Generar' o ingrese un token seguro"
                />
                <button
                  class="btn btn-outline-secondary"
                  type="button"
                  @click="mostrarWebhookToken = !mostrarWebhookToken"
                  :title="mostrarWebhookToken ? 'Ocultar token' : 'Mostrar token'"
                >
                  <i :class="mostrarWebhookToken ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                </button>
                <button
                  class="btn btn-outline-secondary"
                  type="button"
                  @click="copiarAlPortapapeles(localAjustes.whatsapp_webhook_token, 'Verify Token copiado')"
                  title="Copiar Verify Token"
                >
                  <i class="mdi mdi-content-copy"></i>
                </button>
                <button
                  class="btn btn-primary"
                  type="button"
                  @click="generarWebhookToken"
                  :disabled="banderaGenerandoToken"
                  title="Generar nuevo token aleatorio"
                >
                  <i class="mdi me-1" :class="banderaGenerandoToken ? 'mdi-concourse-ci mdi-spin' : 'mdi-refresh'"></i>
                  Generar
                </button>
              </div>
              <small class="text-muted">Pegue este token en el campo <b>Identificador de verificación</b> en Meta Developers.</small>
            </div>

            <div class="col-12">
              <div class="alert alert-info py-2 px-3 mb-0 font-size-12 d-flex align-items-center">
                <i class="mdi mdi-information-outline font-size-18 me-2 text-info flex-shrink-0"></i>
                <div>
                  En Meta Developers (<i>WhatsApp &gt; Configuración &gt; Webhook</i>), haga clic en <b>Editar</b>, pegue la URL y el Token, guarde y suscríbase al campo <b><code>messages</code></b> para recibir el estado de entrega y lectura.
                  <span class="d-block mt-1 text-danger fw-semibold">
                    <i class="mdi mdi-alert-circle-outline me-1"></i>Importante: Solo se permite la suscripción al campo <code>messages</code>; otros tipos de suscripción no están permitidos por el momento.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SECCIÓN 4: Envío de Mensaje de Prueba -->
      <div class="card border shadow-sm">
        <div class="card-header py-3 border-bottom d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <i class="mdi mdi-send-check text-primary font-size-22 me-2"></i>
            <div>
              <h6 class="fw-bold mb-0 font-size-15">
                Envía un mensaje de prueba
              </h6>
              <div class="text-muted font-size-12 mt-1">
                Verifica la conectividad con Meta Cloud API enviando la plantilla oficial de prueba (<code>hello_world</code>).
              </div>
            </div>
          </div>
        </div>

        <div class="card-body p-4">
          <div class="row g-3">
            <!-- Número emisor -->
            <div class="col-md-3">
              <label class="form-label fw-bold font-size-13 mb-1">
                Número emisor
              </label>
              <div class="input-group">
                <span class="input-group-text">
                  <i class="mdi mdi-whatsapp text-success font-size-18"></i>
                </span>
                <input
                  type="text"
                  class="form-control"
                  v-model="localAjustes.whatsapp_numero_telefono"
                  placeholder="+52 55 1234 5678"
                />
              </div>
              <small class="text-muted font-size-11">
                Número verificado asociado a la cuenta.
              </small>
            </div>

            <!-- Destinatario -->
            <div class="col-md-3">
              <label class="form-label fw-bold font-size-13 mb-1">
                Destinatario <span class="text-danger">*</span>
              </label>
              <div class="input-group">
                <span class="input-group-text">
                  <i class="mdi mdi-whatsapp text-success font-size-18"></i>
                </span>
                <input
                  type="text"
                  class="form-control"
                  :value="telefonoPrueba"
                  @input="$emit('update:telefono-prueba', $event.target.value); $emit('update:telefonoPrueba', $event.target.value)"
                  placeholder="+52-55-1302-0732"
                />
              </div>
              <small class="text-muted font-size-11">
                Número con lada internacional para recibir la prueba.
              </small>
            </div>

            <!-- Mensaje / Template fijo -->
            <div class="col-md-3">
              <label class="form-label fw-bold font-size-13 mb-1">
                Plantilla API
              </label>
              <div class="input-group">
                <span class="input-group-text">
                  <i class="mdi mdi-lock text-muted font-size-16"></i>
                </span>
                <input
                  type="text"
                  class="form-control font-monospace"
                  value="hello_world"
                  disabled
                  readonly
                />
              </div>
              <small class="text-warning font-size-11">
                <i class="mdi mdi-lock me-1"></i>Plantilla oficial fija: <code>hello_world</code> (no modificable).
              </small>
            </div>

            <!-- Idioma de la API fijo -->
            <div class="col-md-3">
              <label class="form-label fw-bold font-size-13 mb-1">
                Idioma API
              </label>
              <div class="input-group">
                <span class="input-group-text">
                  <i class="mdi mdi-lock text-muted font-size-16"></i>
                </span>
                <input
                  type="text"
                  class="form-control"
                  value="Inglés (EE. UU.) - en_US"
                  disabled
                  readonly
                />
              </div>
              <small class="text-warning font-size-11">
                <i class="mdi mdi-lock me-1"></i>Idioma oficial fijo: <code>en_US</code> (no modificable).
              </small>
            </div>

            <!-- Botón de Acción -->
            <div class="col-12 d-flex justify-content-end pt-2">
              <button
                type="button"
                class="btn btn-primary d-inline-flex align-items-center"
                @click="$emit('enviar-prueba')"
                :disabled="banderaPrueba || !localAjustes.whatsapp_activo"
              >
                <i class="mdi me-1" :class="banderaPrueba ? 'mdi-concourse-ci mdi-spin' : 'mdi-send'"></i>
                {{ banderaPrueba ? 'Enviando mensaje...' : 'Enviar mensaje de prueba' }}
              </button>
            </div>

            <!-- Respuesta en Vivo de Meta Graph API -->
            <div class="col-12" v-if="respuestaPrueba">
              <div class="mt-3 pt-3 border-top">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <span class="fw-bold font-size-13" :class="estadoRespuestaPrueba === 'success' ? 'text-success' : 'text-danger'">
                    <i class="mdi me-1" :class="estadoRespuestaPrueba === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'"></i>
                    {{ estadoRespuestaPrueba === 'success' ? 'Respuesta de Meta API (200 OK):' : 'Error de Meta API:' }}
                  </span>
                  <button type="button" class="btn btn-sm btn-outline-secondary py-0 px-2 font-size-11" @click="$emit('limpiar-respuesta')">
                    <i class="mdi mdi-close me-1"></i> Limpiar respuesta
                  </button>
                </div>
                <pre class="bg-dark text-white p-3 rounded font-monospace font-size-12 mb-0 shadow-sm" style="max-height: 200px; overflow-y: auto;">{{ JSON.stringify(respuestaPrueba, null, 2) }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>

    </fieldset>

    <hr class="mt-4 mb-3">
    <!-- Botones de Acción de la Sección Configuración -->
    <div class="d-flex justify-content-end align-items-center">
      <button class="btn btn-secondary me-2" @click="$router.go(-1)">
        <i class="mdi mdi-chevron-left me-1"></i> Atrás
      </button>
      <button
        type="button"
        class="btn btn-success"
        @click="$emit('guardar')"
        :disabled="banderaSpinner"
      >
        <i
          class="mdi me-1"
          :class="!banderaSpinner ? 'mdi-content-save' : 'mdi-concourse-ci mdi-spin'"
        ></i>
        {{ banderaSpinner ? 'Guardando...' : 'Guardar Configuración' }}
      </button>
    </div>
  </div>
</template>

<script>
import API, { APIWHATSAPP } from '@/API.js'
import SitioSrv from '@/services/SitioSrv.js'

export default {
  name: 'CmpConfiguracionApi',
  props: {
    ajustes: {
      type: Object,
      required: true
    },
    banderaSpinner: {
      type: Boolean,
      default: false
    },
    banderaPrueba: {
      type: Boolean,
      default: false
    },
    telefonoPrueba: {
      type: String,
      default: ''
    },
    respuestaPrueba: {
      type: Object,
      default: null
    },
    estadoRespuestaPrueba: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      mostrarToken: false,
      mostrarSecret: false,
      mostrarWebhookToken: false,
      banderaGenerandoToken: false,
      localAjustes: Object.assign({}, this.ajustes)
    }
  },
  computed: {
    urlWebhook() {
      return `${APIWHATSAPP}/api/dmz/webhook/whatssap`
    }
  },
  watch: {
    ajustes: {
      handler(val) {
        if (val) {
          this.localAjustes = Object.assign({}, val)
        }
      },
      deep: true
    },
    localAjustes: {
      handler(val) {
        this.$emit('update-ajustes', val)
      },
      deep: true
    }
  },
  methods: {
    generarWebhookToken() {
      this.banderaGenerandoToken = true
      SitioSrv.generarWebhookToken().then(response => {
        if (response.data && response.data.token) {
          this.localAjustes.whatsapp_webhook_token = response.data.token
          iu.msg.success('Token de verificación generado con éxito. Recuerde guardar la configuración.')
        }
      }).catch(err => {
        let msg = (err.response && err.response.data && err.response.data.message) || 'Error al generar token'
        // Fallback local en caso de error de red
        let fallbackToken = Array.from(crypto.getRandomValues(new Uint8Array(24)))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('')
        this.localAjustes.whatsapp_webhook_token = fallbackToken
        iu.msg.success('Token generado localmente. Recuerde guardar la configuración.')
      }).finally(() => {
        this.banderaGenerandoToken = false
      })
    },
    copiarAlPortapapeles(texto, mensaje = 'Copiado al portapapeles') {
      if (!texto) {
        iu.msg.warning('No hay contenido para copiar')
        return
      }
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(texto).then(() => {
          iu.msg.success(mensaje)
        }).catch(() => {
          this.copiarFallback(texto, mensaje)
        })
      } else {
        this.copiarFallback(texto, mensaje)
      }
    },
    copiarFallback(texto, mensaje) {
      let input = document.createElement('textarea')
      input.value = texto
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      iu.msg.success(mensaje)
    }
  }
}
</script>

<style scoped>
.style-pointer {
  cursor: pointer;
}
</style>
